import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { HrEmployeeDocument } from '../../../hr/hr.models';
import { MyHrWorkspaceService } from '../shared/my-hr-workspace.service';
import { labelize, statusSeverity } from '../shared/my-hr-workspace.utils';

@Component({
  selector: 'app-my-hr-documents',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    TableModule,
    TagModule,
    TextareaModule,
    PageDataPanelComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './my-hr-documents.component.html',
  styleUrls: ['./my-hr-documents.component.scss']
})
export class MyHrDocumentsComponent implements OnInit {
  private readonly workspaceService = inject(MyHrWorkspaceService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);

  readonly canManageSelf = this.permissionService.hasPermission(app_permissions.hr.self.manage);

  loading = false;
  uploading = false;
  documents: HrEmployeeDocument[] = [];
  documentForm: HrEmployeeDocument = {};

  ngOnInit(): void {
    this.load();
  }

  statusSeverity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    return statusSeverity(value);
  }

  label(value?: string): string {
    return labelize(value);
  }

  upload(): void {
    if (!this.canManageSelf || !this.documentForm.fileUrl || !(this.documentForm.documentCode || this.documentForm.documentName)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Documents',
        detail: 'Document code or name plus a file URL are required.'
      });
      return;
    }
    this.uploading = true;
    this.workspaceService.uploadDocument(this.documentForm).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: 'Document submitted for HR review.' });
        this.documentForm = {};
        this.workspaceService.requestRefresh();
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Documents', detail: 'Document could not be uploaded.' });
      },
      complete: () => {
        this.uploading = false;
      }
    });
  }

  private load(): void {
    this.loading = true;
    this.workspaceService.loadDocuments().subscribe({
      next: documents => {
        this.documents = documents;
      },
      error: () => {
        this.documents = [];
      },
      complete: () => {
        this.loading = false;
      }
    });
  }
}
